import { useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { styles } from "../styles";
import { OptionCard } from "../common/OptionCard";
import { BsHouse, BsHouses } from "react-icons/bs";
import {
  useFacilitiesByUserId,
  useGetCurrentLocalFacilityId,
} from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import { Clubhouse_LeagueFacilityScope } from "../../enums";
import type { LeagueFormValues } from "../leagueFormSchema";
import { useFormContext, Controller, useWatch } from "react-hook-form";

const { THIS_FACILITY, ANY_OF_MY_FACILITIES } = Clubhouse_LeagueFacilityScope;

/**
 * Only operators with more than one facility see this step; the wizard drops it
 * otherwise and the league is created at the operator's current facility.
 *
 * "Multiple facilities" opens the same checklist Advanced Setup's Location tab
 * uses, seeded with the operator's own facility — Guided used to send *every*
 * facility the operator could see, which is a different league than the one the
 * words promise.
 */
export function LeagueFacilityStep() {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<LeagueFormValues>();

  const scope = useWatch({ control, name: "facilityScope" });
  const selected =
    useWatch({ control, name: "participatingClubhouseFacilityIds" }) ?? [];

  const [userFacilities] = useFacilitiesByUserId(getUserIdFromToken());
  const homeFacilityId = useGetCurrentLocalFacilityId();

  const facilities = useMemo(
    () => userFacilities.filter((f) => f.clubhouseFacilityId != null),
    [userFacilities],
  );
  const homeClubhouseId = useMemo(
    () => facilities.find((f) => f.id === homeFacilityId)?.clubhouseFacilityId,
    [facilities, homeFacilityId],
  );

  // The operator's own facility is checked to begin with, so "Multiple
  // facilities" starts from where they are rather than from nothing.
  useEffect(() => {
    if (selected.length > 0 || facilities.length === 0) return;
    const seed = homeClubhouseId ?? facilities[0]!.clubhouseFacilityId;
    if (seed == null) return;
    setValue("participatingClubhouseFacilityIds", [seed]);
  }, [selected.length, facilities, homeClubhouseId, setValue]);

  const toggle = (id: number) => {
    const on = selected.includes(id);
    // At least one facility has to stay checked — a league with nowhere to play
    // is not a league the API will accept.
    if (on && selected.length === 1) return;
    setValue(
      "participatingClubhouseFacilityIds",
      on ? selected.filter((x) => x !== id) : [...selected, id],
      { shouldValidate: true },
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <Controller
        name="facilityScope"
        control={control}
        render={({ field }) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <OptionCard
              icon={<BsHouse className="size-8" />}
              title="This facility only"
              description="Only at your current facility."
              selected={field.value === THIS_FACILITY}
              onClick={() => field.onChange(THIS_FACILITY)}
            />
            <OptionCard
              icon={<BsHouses className="size-8" />}
              title="Multiple facilities"
              description="Limited to facilities you choose."
              selected={field.value === ANY_OF_MY_FACILITIES}
              onClick={() => field.onChange(ANY_OF_MY_FACILITIES)}
            />
          </div>
        )}
      />

      {scope === ANY_OF_MY_FACILITIES && (
        <div className="flex flex-col gap-2">
          <p className="poppins text-sm font-bold text-text-primary">
            Choose facilities
          </p>
          <ul className="flex flex-col gap-2">
            {facilities.map((f) => {
              const id = f.clubhouseFacilityId as number;
              const on = selected.includes(id);
              const isLast = on && selected.length === 1;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    disabled={isLast}
                    aria-pressed={on}
                    aria-label={f.name}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                      on
                        ? "border-primary/40 bg-primary/[0.04]"
                        : "border-border bg-surface hover:border-primary/20 hover:bg-muted/40",
                      isLast
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors",
                        on
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/60 bg-surface",
                      )}
                    >
                      {on && <Check className="size-3.5 stroke-[3]" />}
                    </span>
                    <span className="poppins text-sm font-semibold tracking-tight text-text-primary">
                      {f.name}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {errors.facilityScope && (
        <p className={styles.wizardErrorCls}>{errors.facilityScope.message}</p>
      )}
    </div>
  );
}
